"use strict";
/**
 * `RPM-083` — the actor is a `user` id, and a nurse's name is not representable (D5, N17).
 *
 * §6's MUST: *"Every actor reference is a `user` id. Free-text nurse names are **not representable**
 * anywhere in this domain. The old system's free-text `nurse_name` made the same person two people
 * to any report, and that defect is closed by construction rather than by discipline."*
 *
 * Closed by construction means two checks, because the defect can arrive from two directions:
 *
 *   1. **The catalogue.** A column that stores a person's name as text, on a row whose subject is an
 *      actor. The old system's `nurse_name` was exactly this. The check reads every textual column
 *      in `public` and names the ones that look like a person's name; the legitimate ones — the
 *      person register, the account's own name, the person NOTIFIED — are enumerated by name with
 *      the reason, the way §9.3 enumerates its eight excluded tables, so a tenth is a decision
 *      somebody makes rather than a silent widening.
 *   2. **The contract.** A route input that accepts one. Two halves, because on Zod 4.5.4 a plain
 *      `z.object()` STRIPS an unknown key and only a strict object REJECTS it — measured, not
 *      assumed. So "rejected as an unknown field by the strict contract" needs both: no input
 *      declares an actor-name field, AND a request body refuses the fields it does not declare.
 *
 * Both entry points REFUSE an empty population rather than reporting a clean run over nothing.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.PERSON_NAME_COLUMNS_THAT_ARE_NOT_AN_ACTOR = void 0;
exports.looksLikePersonName = looksLikePersonName;
exports.findActorNameColumns = findActorNameColumns;
exports.scanActorContract = scanActorContract;
/** Types that can hold a spelling. A uuid cannot, which is the whole point of the requirement. */
const TEXTUAL = new Set(["text", "character varying", "character", "citext", "json", "jsonb"]);
/**
 * Words that make a column ABOUT A PERSON rather than about a place, a table or an organisation.
 *
 * This is what separates `person_tenant_affiliations.display_name` from
 * `source_systems.display_name`: the same column name, and only one of them is a human. The table
 * name is read as part of the same token stream for exactly that reason.
 */
const PERSON_WORDS = new Set([
    "nurse",
    "nurses",
    "clinician",
    "clinicians",
    "physician",
    "practitioner",
    "provider",
    "staff",
    "actor",
    "user",
    "users",
    "person",
    "people",
    "operator",
    "author",
    "signer",
    "recorder",
    "caller",
    "reviewer",
    "approver",
    "attendant",
    "contact",
]);
/** Words that make a column a SPELLING of something. */
const NAME_WORDS = new Set([
    "name",
    "names",
    "fullname",
    "firstname",
    "lastname",
    "surname",
    "forename",
]);
/**
 * Does `<table>.<column>` look like a person's name held as text?
 *
 * Two shapes are caught: a person word anywhere in the stream together with a name word, and a
 * person word immediately followed by `text` — `contact_person_text` is the second, and the old
 * system's `nurse_name` is the first.
 */
function looksLikePersonName(table, column) {
    const tokens = `${table}_${column}`
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter(Boolean);
    const hasPerson = tokens.some((t) => PERSON_WORDS.has(t));
    if (!hasPerson)
        return false;
    if (tokens.some((t) => NAME_WORDS.has(t)))
        return true;
    return tokens.some((t, i) => PERSON_WORDS.has(t) && tokens[i + 1] === "text");
}
/**
 * The person-name columns that are NOT an actor's, each with the reason it is right.
 *
 * Keyed `table.column`. `findActorNameColumns` reports an entry the catalogue no longer holds, or
 * that the pattern no longer flags, because an exemption nobody prunes is an exemption nobody reads.
 */
exports.PERSON_NAME_COLUMNS_THAT_ARE_NOT_AN_ACTOR = {
    "user.name": "Better Auth's own account name. RPM-083 says the actor IS this account, and RPM-102 requires administrative screens to show `user.name` and `user.email`; N17 says the table is not modified.",
    "people.family_name": "The person register (RPM-102). A `people` row is an administered entity, it reaches nothing, and no clinical route joins it.",
    "people.given_name": "The person register (RPM-102), as above.",
    "people.middle_name": "The person register (RPM-102), as above.",
    "people.name_suffix": "The person register (RPM-102), as above.",
    "people.preferred_name": "The person register (RPM-102), as above.",
    "person_tenant_affiliations.display_name": "How the person register names one human inside one customer (RPM-102). Not an actor reference: reach comes from `role_assignments.user_id`.",
    "rpm_alerts.notify_person_name": "The person NOTIFIED, typed on this alert every time and never accumulated into a directory (§4.3.7, S4-1-D40). The opposite of the old free-text `nurse_name`: it names the contact, never the actor, and `created_by` beside it is the actor.",
    "rpm_alert_notify_people.person_name": "The same person as `rpm_alerts.notify_person_name`, at position two and beyond. §4.3.10: the alert's own three notify columns are the first of the list and these rows are the whole of it, typed on this workup with no directory behind them. It names the contact, never the actor, and `created_by` on the same row is the actor (J-413).",
    "notification_attempts.contact_person_text": "Who was reached on that one attempt, recorded as it was said. Same reason as `notify_person_name`: the attempt's actor is `attempted_by`, a `user` id.",
};
/**
 * Every textual column that looks like a person's name and is not enumerated above.
 *
 * @throws when the catalogue is empty, which means the query is wrong rather than that the schema
 * has no columns.
 */
function findActorNameColumns(catalogue) {
    if (catalogue.length === 0)
        throw new Error("ACTOR_NAME_AS_TEXT: the catalogue returned no column at all. This schema declares hundreds, so an empty result is a broken query and not a clean database.");
    const excused = exports.PERSON_NAME_COLUMNS_THAT_ARE_NOT_AN_ACTOR;
    const flagged = catalogue.filter((c) => TEXTUAL.has(c.type.toLowerCase()) && looksLikePersonName(c.table, c.column));
    const flaggedKeys = new Set(flagged.map((c) => `${c.table}.${c.column}`));
    const findings = flagged
        .filter((c) => !Object.hasOwn(excused, `${c.table}.${c.column}`))
        .map((c) => ({
        code: "ACTOR_NAME_AS_TEXT",
        table: c.table,
        column: c.column,
        detail: `${c.table}.${c.column} is ${c.type} and reads as a person's name; an actor is a \`user\` id and nothing else (RPM-083, D5). If this column names someone who is not the actor, add it to PERSON_NAME_COLUMNS_THAT_ARE_NOT_AN_ACTOR with the reason.`,
    }));
    const staleExemptions = Object.keys(excused).filter((k) => !flaggedKeys.has(k));
    return { findings, inspected: catalogue.length, staleExemptions };
}
/**
 * The request fields that write one of the non-actor person-name columns above, keyed
 * `"<METHOD> <path> <field>"`, each naming its column so the reason is written once, there.
 * `contactPersonText` is who was reached on an attempt (`RPM-040`, `J-229`); the attempt's actor is
 * the session's account, written to `attempted_by`, and no field of the body names it. A held call's
 * close writes the same column on every resident's Reached row (`RPM-043`, `J-231`).
 */
const PERSON_NAME_INPUTS_THAT_ARE_NOT_AN_ACTOR = {
    "POST /api/rpm/v1/alerts/{alertId}/attempts contactPersonText": "notification_attempts.contact_person_text",
    "POST /api/rpm/v1/held-calls/{heldCallId}/close contactPersonText": "notification_attempts.contact_person_text",
};
/** Whether a flagged input writes a column the register exempts, with its reason. */
function exemptInput(operation, field, seen) {
    const key = `${operation} ${field}`;
    const column = PERSON_NAME_INPUTS_THAT_ARE_NOT_AN_ACTOR[key];
    if (column === undefined || exports.PERSON_NAME_COLUMNS_THAT_ARE_NOT_AN_ACTOR[column] === undefined)
        return false;
    seen.add(key);
    return true;
}
const isObject = (v) => typeof v === "object" && v !== null;
/** Follows one `#/`-style local reference. Returns the node itself when it is not a reference. */
function deref(schema, doc) {
    if (!isObject(schema))
        return schema;
    const ref = schema.$ref;
    if (typeof ref !== "string" || !ref.startsWith("#/"))
        return schema;
    let node = doc;
    for (const part of ref.slice(2).split("/")) {
        node = isObject(node) ? node[part.replace(/~1/g, "/").replace(/~0/g, "~")] : undefined;
    }
    return node;
}
/** Every property name a schema can reach, following refs and the composition keywords. */
function propertyNames(schema, doc, seen) {
    const node = deref(schema, doc);
    if (!isObject(node) || seen.has(node))
        return [];
    seen.add(node);
    const out = [];
    if (isObject(node.properties))
        for (const [name, sub] of Object.entries(node.properties)) {
            out.push(name, ...propertyNames(sub, doc, seen));
        }
    for (const key of ["items", "additionalProperties", "not"]) {
        out.push(...propertyNames(node[key], doc, seen));
    }
    for (const key of ["allOf", "anyOf", "oneOf"]) {
        const branch = node[key];
        if (Array.isArray(branch))
            for (const sub of branch)
                out.push(...propertyNames(sub, doc, seen));
    }
    return out;
}
const HTTP_METHODS = ["get", "put", "post", "delete", "options", "head", "patch", "trace"];
/** `camelCase` -> `snake_case`, so one pattern judges both spellings the stack uses. */
const toSnake = (name) => name.replace(/([a-z0-9])([A-Z])/g, "$1_$2").toLowerCase();
/**
 * Two assertions over the generated contract, and the count of what was walked so a run over a
 * document with no inputs cannot pass silently.
 *
 *   - `nameFields`: an input that declares an actor's name. The old system's `nurse_name` arriving
 *     on the wire.
 *   - `looseBodies`: a request body object that does not declare `additionalProperties: false`.
 *     Measured on Zod 4.5.4: `z.object()` STRIPS `nurseName` and reports success, while
 *     `z.strictObject()` rejects it with `unrecognized_keys`. Row 2 says "rejected", so a body that
 *     strips is not a body that satisfies it.
 *
 * @throws when the document declares no input schema at all.
 */
function scanActorContract(document) {
    const doc = isObject(document) ? document : {};
    const nameFields = [];
    const looseBodies = [];
    let inspected = 0;
    let bodies = 0;
    const exempted = new Set();
    const paths = isObject(doc.paths) ? doc.paths : {};
    for (const [path, item] of Object.entries(paths)) {
        if (!isObject(item))
            continue;
        for (const method of HTTP_METHODS) {
            const op = item[method];
            if (!isObject(op))
                continue;
            const operation = `${method.toUpperCase()} ${path}`;
            for (const p of Array.isArray(op.parameters) ? op.parameters : []) {
                if (!isObject(p))
                    continue;
                inspected += 1;
                const where = typeof p.in === "string" ? p.in : "parameter";
                const declared = [
                    typeof p.name === "string" ? p.name : "",
                    ...propertyNames(p.schema, doc, new Set()),
                ].filter(Boolean);
                for (const field of declared)
                    if (looksLikePersonName("request", toSnake(field)))
                        nameFields.push({ operation, location: `${where} parameter`, field });
            }
            const body = op.requestBody;
            if (!isObject(body) || !isObject(body.content))
                continue;
            for (const [media, entry] of Object.entries(body.content)) {
                if (!isObject(entry))
                    continue;
                inspected += 1;
                bodies += 1;
                for (const field of propertyNames(entry.schema, doc, new Set()))
                    if (looksLikePersonName("request", toSnake(field)) &&
                        !exemptInput(operation, field, exempted))
                        nameFields.push({ operation, location: `request body (${media})`, field });
                const schema = deref(entry.schema, doc);
                if (isObject(schema) && schema.type === "object" && schema.additionalProperties !== false)
                    looseBodies.push({
                        operation,
                        media,
                        detail: "declares no `additionalProperties: false`, so on Zod 4.5.4 an undeclared `nurseName` is STRIPPED and the request succeeds. Row 2 of RPM-083 says it is rejected, which needs a strict object.",
                    });
            }
        }
    }
    if (inspected === 0)
        throw new Error("RPM-083: the contract declares no input schema at all. A scan over nothing reports on nothing, so it fails instead of passing.");
    const staleInputExemptions = Object.keys(PERSON_NAME_INPUTS_THAT_ARE_NOT_AN_ACTOR).filter((key) => !exempted.has(key));
    return { nameFields, looseBodies, inspected, bodies, staleInputExemptions };
}
//# sourceMappingURL=actor-identity.js.map